import React, { useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  ClipboardCheck,
  Camera,
  LayoutDashboard,
  SunMedium,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { typography } from "../../theme";
import "../../styles/layout.css";
import { useAuth } from "../../context/AuthContext";
import OnboardingTour from "../OnboardingTour";
import earlySpotMark from "../EarlySpot.png";

type AppLayoutProps = {
  children: React.ReactNode;
};

type NavItem = {
  label: string;
  path: string;
  Icon: LucideIcon;
  tourId: string;
};

const navItems: NavItem[] = [
  {
    label: "Dashboard",
    path: "/dashboard",
    Icon: LayoutDashboard,
    tourId: "dashboard",
  },
  { label: "Scan", path: "/scan", Icon: Camera, tourId: "scan" },
  {
    label: "Results",
    path: "/results",
    Icon: ClipboardCheck,
    tourId: "results",
  },
  { label: "UV Index", path: "/uv-index", Icon: SunMedium, tourId: "uv-index" },
  { label: "Profile", path: "/profile", Icon: UserRound, tourId: "profile" },
];

const renderNavLinks = (baseClass: string, iconClass: string) =>
  navItems.map(({ label, path, Icon, tourId }) => (
    <NavLink
      key={path}
      to={path}
      className={({ isActive }) =>
        `${baseClass}${isActive ? ` ${baseClass}--active` : ""}`
      }
      data-tour={tourId}
    >
      <span className={iconClass} aria-hidden>
        <Icon size={20} strokeWidth={2.2} />
      </span>
      <span>{label}</span>
    </NavLink>
  ));

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we have tokens in the URL fragment (Cognito redirect)
    // but we are NOT on the callback page.
    if (
      location.hash &&
      (location.hash.includes("access_token=") ||
        location.hash.includes("id_token=")) &&
      !location.pathname.includes("/oauth/callback")
    ) {
      console.log("Detected auth tokens on non-callback page, redirecting...");
      navigate(`/oauth/callback${location.hash}`, { replace: true });
    }
  }, [location, navigate]);

  const handleAuthClick = () => {
    if (user) {
      logout();
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="app-shell">
      <aside className="app-shell__sidebar">
        <Link
          to="/dashboard"
          className="app-shell__brand"
          aria-label="EarlySpot home"
        >
          <img
            src={earlySpotMark}
            alt="EarlySpot"
            className="app-shell__brand-logo"
            draggable={false}
          />
        </Link>
        <nav className="app-shell__nav app-shell__nav--desktop">
          {renderNavLinks("app-shell__nav-item", "app-shell__nav-icon")}
        </nav>
      </aside>
      <div className="app-shell__main">
        <div className="app-shell__top-bar">
          <Link
            to="/dashboard"
            className="app-shell__top-brand"
            aria-label="EarlySpot home"
          >
            <img
              src={earlySpotMark}
              alt="EarlySpot"
              className="app-shell__top-logo"
              draggable={false}
            />
          </Link>
        </div>
        <header className="app-shell__header">
          <div>
            <h1
              style={{
                fontSize: typography.sizes.lg,
                fontWeight: typography.weights.semibold,
              }}
            >
              Hello, {user ? user.name || user.id.split("@")[0] : "Guest"}
            </h1>
            <p className="app-shell__subtitle">
              {user
                ? "Your scans are tied to your account."
                : "You're in guest mode."}
            </p>
          </div>
          <button className="app-shell__auth-chip" onClick={handleAuthClick}>
            {user ? "Logout" : "Login"}
          </button>
        </header>
        <main className="app-shell__content">{children}</main>
        <OnboardingTour />
        <footer className="app-shell__footer" />
      </div>
      <nav className="app-shell__bottom-nav" aria-label="Primary navigation">
        {renderNavLinks(
          "app-shell__bottom-nav-item",
          "app-shell__bottom-nav-icon"
        )}
      </nav>
    </div>
  );
};

export default AppLayout;
